'use client';

import {useDevConfig} from 'common/dev';
import {useAuth} from 'iq-auth';
import {ShareButton} from 'iq-interview_guides/components/share_button';
import type {ColorVariant, TakeHomeListItem} from 'iq-takehomes/types';
import {
  Activity,
  BarChart2,
  BarChart3,
  Brain,
  Briefcase,
  Building2,
  Calendar,
  Car,
  Check,
  Cloud,
  Coffee,
  Cpu,
  CreditCard,
  Database,
  DollarSign,
  FileText,
  Globe,
  Hotel,
  Lightbulb,
  LineChart,
  LockOpen,
  type LucideIcon,
  Mail,
  Map,
  MessageSquare,
  Package,
  PieChart,
  Plane,
  Rocket,
  Search,
  ShoppingCart,
  Table2,
  Target,
  TrendingDown,
  TrendingUp,
  Truck,
  Users,
  Zap,
} from 'lucide-react';
import {trackTakeHomeCardClicked} from 'iq-takehomes/tracking';
import Image from 'next/image';
import Link from 'next/link';
import type {ReactNode} from 'react';

const COLOR_VARIANTS: Record<ColorVariant, {bg: string; text: string}> = {
  lavender: {bg: 'bg-[#dcd3f5]', text: 'text-[#2d1a6e]'},
  lemon: {bg: 'bg-[#f5f0c0]', text: 'text-[#4a3800]'},
  mint: {bg: 'bg-[#c8eee2]', text: 'text-[#0c3d2e]'},
  sky: {bg: 'bg-[#c8e4f8]', text: 'text-[#0b2e4a]'},
  pink: {bg: 'bg-[#f5d0e8]', text: 'text-[#5a1040]'},
  peach: {bg: 'bg-[#f8d9c4]', text: 'text-[#5a2a0c]'},
  sage: {bg: 'bg-[#cee8d4]', text: 'text-[#1a3d24]'},
  lilac: {bg: 'bg-[#e2d0f5]', text: 'text-[#3a1060]'},
  coral: {bg: 'bg-[#fbd3ce]', text: 'text-[#5c1a12]'},
  aqua: {bg: 'bg-[#c7ecec]', text: 'text-[#0b3b3b]'},
  sand: {bg: 'bg-[#ece0c8]', text: 'text-[#4a3416]'},
  slate: {bg: 'bg-[#d8dfe8]', text: 'text-[#1e2b3a]'},
};

// take-homes store the icon as a PascalCase lucide name (e.g. "Hotel", "BarChart2"),
// set in the annotations admin. We resolve against this curated allowlist rather than
// lucide-react/dynamic: DynamicIcon pulls the whole ~1500-icon import map into the
// module graph, which OOMs the dev server. Statically importing only these keeps the
// bundle small. Unknown/empty names fall back to TrendingDown.
// ponytail: hardcoded map; grow the list if annotators need more icons.
const TAKE_HOME_ICONS: Record<string, LucideIcon> = {
  Activity,
  BarChart2,
  BarChart3,
  Brain,
  Briefcase,
  Building2,
  Calendar,
  Car,
  Cloud,
  Coffee,
  Cpu,
  CreditCard,
  Database,
  DollarSign,
  FileText,
  Globe,
  Hotel,
  Lightbulb,
  LineChart,
  Mail,
  Map,
  MessageSquare,
  Package,
  PieChart,
  Plane,
  Rocket,
  Search,
  ShoppingCart,
  Table2,
  Target,
  TrendingDown,
  TrendingUp,
  Truck,
  Users,
  Zap,
};

// titleSize maps to a heading tag + classes. lg keeps one <h1> per detail page; md
// is the default list-card title.
const TITLE_STYLES = {
  lg: {tag: 'h1', className: 'text-2xl sm:text-[30px] font-bold leading-snug'},
  md: {tag: 'h2', className: 'text-base font-extrabold leading-snug'},
} as const;

function SubmissionCheck({submitted}: {submitted: boolean}) {
  if (!submitted) return null;
  return (
    <span className='w-4.5 h-4.5 rounded-full bg-white flex items-center justify-center shrink-0 relative -top-px'>
      <Check className='w-3 h-3 text-green-500' strokeWidth={3} />
    </span>
  );
}

// UnlockedBadge marks a take-home the limited-quota user already spent a slot on.
function UnlockedBadge({unlocked}: {unlocked: boolean}) {
  if (!unlocked) return null;
  return (
    <span title='Unlocked' aria-label='Unlocked' className='flex items-center shrink-0'>
      <LockOpen className='w-3.5 h-3.5 opacity-60' strokeWidth={2} />
    </span>
  );
}

function OverflowBadge({labels}: {labels: Array<string>}) {
  if (labels.length === 0) return null;
  return (
    <div className='relative group'>
      <span className='text-xs text-iq-grey-dark cursor-default'>+{labels.length}</span>
      <div className='absolute top-full left-1/2 -translate-x-1/2 mt-1.5 z-10 hidden group-hover:block bg-iq-black text-white text-xs rounded-lg px-2.5 py-1.5 whitespace-nowrap shadow-lg pointer-events-none'>
        {labels.join(', ')}
      </div>
    </div>
  );
}

// CardShell wraps the card in a navigating <Link> by default; the detail page opts
// out (linkToDetail=false) since the card already represents the current page.
function CardShell({
  linkToDetail,
  slug,
  className,
  children,
}: {
  linkToDetail: boolean;
  slug: string;
  className: string;
  children: ReactNode;
}) {
  if (!linkToDetail) return <div className={className}>{children}</div>;
  return (
    <Link
      href={`/takehomes/${slug}`}
      onClick={() => { void trackTakeHomeCardClicked(slug); }}
      className={className}>
      {children}
    </Link>
  );
}

interface Props {
  takeHome: TakeHomeListItem;
  titleSize?: 'lg' | 'md';
  showShareButton?: boolean;
  collapseCompanies?: boolean;
  collapsePositions?: boolean;
  collapseTopics?: boolean;
  linkToDetail?: boolean;
}

export function TakeHomeCard({
  takeHome,
  titleSize = 'md',
  showShareButton = false,
  collapseCompanies = true,
  collapsePositions = true,
  collapseTopics = true,
  linkToDetail = true,
}: Props) {
  const colors = COLOR_VARIANTS[takeHome.color_variant];
  const {cardStyle} = useDevConfig();
  const isInset = cardStyle === 'inset';
  const {user, isPremium} = useAuth();
  // Only limited-quota users see unlock state; unlimited users have nothing to unlock.
  const showUnlocked = takeHome.unlocked && !isPremium && !user.isAdmin;
  const titleStyle = TITLE_STYLES[titleSize];
  const Heading = titleStyle.tag;
  const Icon = TAKE_HOME_ICONS[takeHome.lucide_icon] ?? TrendingDown;

  // Collapse to a visible slice + an overflow badge, unless the flag turns it off.
  const visibleCompanies = collapseCompanies ? takeHome.companies.slice(0, 1) : takeHome.companies;
  const hiddenCompanies = collapseCompanies ? takeHome.companies.slice(1) : [];
  const visiblePositions = collapsePositions ? takeHome.positions.slice(0, 2) : takeHome.positions;
  const hiddenPositions = collapsePositions ? takeHome.positions.slice(2) : [];
  const visibleTopics = collapseTopics ? takeHome.topics.slice(0, 2) : takeHome.topics;
  const hiddenTopics = collapseTopics ? takeHome.topics.slice(2) : [];

  // Hover-lift only when the card navigates.
  const shellClassName = `flex flex-col h-full bg-white border border-iq-grey-light rounded-2xl shadow-sm${linkToDetail ? ' hover:-translate-y-0.5 hover:shadow-lg transition-all' : ''}${isInset ? ' p-3' : ''}`;

  return (
    <CardShell linkToDetail={linkToDetail} slug={takeHome.slug} className={shellClassName}>
      <div
        className={`flex-1 ${colors.bg} ${colors.text} px-5 pt-5 pb-4 flex flex-col gap-5${isInset ? ' rounded-xl' : ' rounded-t-2xl'}`}>
        <div className='flex items-start justify-between gap-2'>
          <Icon className='w-9 h-9 shrink-0' strokeWidth={1.5} />
          <div className='flex items-center gap-1.5 pt-1'>
            <span className='text-xs font-medium opacity-60'>~{takeHome.estimated_hours} hrs</span>
            <UnlockedBadge unlocked={showUnlocked} />
            <SubmissionCheck submitted={takeHome.own_solution_submitted} />
            {showShareButton && (
              // Eat the click so the surrounding card <Link> doesn't navigate. The fixed
              // h-9 gives the h-full share button a height to stretch into (the hero relies
              // on an items-stretch sibling for this).
              <div className='ml-3 flex h-9' onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                <ShareButton text={takeHome.title} tone='onDark' />
              </div>
            )}
          </div>
        </div>
        {takeHome.topics.length > 0 && (
          <div className='flex flex-wrap items-center gap-1.5'>
            {visibleTopics.map((topic) => (
              <span
                key={topic.slug}
                className='text-xs font-semibold px-2.5 py-1 bg-black/10 rounded-full'>
                {topic.name}
              </span>
            ))}
            <OverflowBadge labels={hiddenTopics.map((t) => t.name)} />
          </div>
        )}
        <div className='flex flex-col gap-1'>
          <Heading className={titleStyle.className}>{takeHome.title}</Heading>
          {/* Full summary on detail/preview pages; clamped on list cards. */}
          {takeHome.summary && (
            <p className={`text-sm opacity-60${linkToDetail ? ' line-clamp-2' : ''}`}>
              {takeHome.summary}
            </p>
          )}
        </div>
      </div>
      <div className='px-4 py-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2'>
        <div className='flex flex-wrap items-center gap-2 shrink-0 max-w-full'>
          {visibleCompanies.map((co) => (
            <div key={co.slug} className='flex items-center gap-1.5 min-w-0'>
              <Image
                src={`https://cdn.brandfetch.io/${co.website}?c=1idvO3y7zFCQvl4O4eh`}
                alt={co.label}
                width={18}
                height={18}
                className='rounded object-contain shrink-0'
                unoptimized
              />
              <span className='text-xs font-semibold text-iq-grey-dark truncate'>{co.label}</span>
            </div>
          ))}
          <OverflowBadge labels={hiddenCompanies.map((c) => c.label)} />
        </div>
        {takeHome.positions.length > 0 && (
          <div className='flex flex-wrap items-center gap-1.5 shrink-0 max-w-full'>
            {visiblePositions.map((pos) => (
              <span
                key={pos.slug}
                className='text-xs font-semibold px-2.5 py-1 bg-iq-grey-light text-iq-grey-dark rounded-full whitespace-nowrap'>
                {pos.label}
              </span>
            ))}
            <OverflowBadge labels={hiddenPositions.map((p) => p.label)} />
          </div>
        )}
      </div>
    </CardShell>
  );
}
